import { COMMENT_BATCH_SIZE } from '@confluence/comments-data';
import type { AnnotationStatus, CommentData } from '@confluence/comments-data';
import type { PageInfoNode } from '@confluence/inline-comments-queries';

export const scrollCommentsPanel = ({
	containerId,
	commentMarkerRef,
}: {
	containerId: string;
	commentMarkerRef: string;
}) => {
	const container = document.querySelector(`[data-testid='${containerId}']`);
	if (container) {
		const element = document.querySelector(`[data-testid='${commentMarkerRef}']`);
		if (element) {
			element.scrollIntoView({ behavior: 'smooth', block: 'start' });
		}
	}
};

export const getAnnotationsToLoad = (orderedActiveAnnotationIdList: AnnotationStatus[]) => {
	return orderedActiveAnnotationIdList
		.slice(0, Math.min(orderedActiveAnnotationIdList.length, COMMENT_BATCH_SIZE))
		.filter((item) => !item.isLoaded) // only get the ones that are not loaded yet
		.map((item) => item.annotationId);
};

// organize a list of open comment threads that we have data for
export const getOpenCommentThreads = ({
	inlineCommentsDataMap,
	orderedActiveAnnotationIdList,
}: {
	inlineCommentsDataMap: Record<string, CommentData>;
	orderedActiveAnnotationIdList: AnnotationStatus[];
}) => {
	const openCommentThreads: CommentData[] = [];
	const annotations = orderedActiveAnnotationIdList.filter(
		(annotation) => annotation.annotationId in inlineCommentsDataMap,
	);
	annotations.forEach((annotation) => {
		openCommentThreads.push(inlineCommentsDataMap[annotation.annotationId]);
	});
	return openCommentThreads;
};

// organize a list of unread comment threads that we have data for
export const getUnreadCommentThreads = ({
	numUnreadComments,
	inlineCommentsDataMap,
	orderedActiveAnnotationIdList,
}: {
	numUnreadComments: number;
	inlineCommentsDataMap: Record<string, CommentData>;
	orderedActiveAnnotationIdList: AnnotationStatus[];
}) => {
	const unreadCommentThreads: CommentData[] = [];
	if (numUnreadComments > 0 && Object.keys(inlineCommentsDataMap).length > 0) {
		const annotations = orderedActiveAnnotationIdList.filter(
			(annotation) => annotation.annotationId in inlineCommentsDataMap,
		);
		annotations.forEach((annotation) => {
			const comment = inlineCommentsDataMap[annotation.annotationId];
			if (comment) {
				if (comment.isUnread) {
					unreadCommentThreads.push(comment); // parent comment contains unread
				} else {
					for (const reply of comment.replies) {
						if (reply.isUnread) {
							unreadCommentThreads.push(comment); // parent comment contains unread
							break; // already confirmed this comment thread contains unread
						}
					}
				}
			}
		});
	}
	return unreadCommentThreads;
};

export const extractPageInfo = (pageInfo: PageInfoNode | null | undefined) => {
	const pageId = pageInfo?.id || '';
	const operations = pageInfo?.operations || [];
	const pageType = pageInfo?.type ?? '';
	const hasMediaUploadPermissions = operations.some(
		(op) => op?.operation === 'update' && op?.targetType === pageType,
	);
	return {
		pageId,
		hasMediaUploadPermissions,
		pageType,
	};
};
